import React, { FC, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { CompanyDetails, CompanyVerificationStatus } from '../../types/api';
import { CompanyProfileData } from '../../types/components';
import companyService  from '../../services/company.service';
import Loader from '../../components/ui/Loader';

const CompanyProfileSummary: FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [companyData, setCompanyData] = useState<CompanyProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (!companyId) {
        setError("Company ID is missing.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await companyService.getCompanyDetails(companyId);
        const companyDetails = response.data;

        let otherDetailsParsed: any = {};
        if (companyDetails.otherDetails) {
          try {
            otherDetailsParsed = JSON.parse(companyDetails.otherDetails);
          } catch (parseError) {
            console.error("Error parsing otherDetails:", parseError);
          }
        }

        const mappedCompanyData: CompanyProfileData = {
          companyName: companyDetails.companyName,
          companyId: companyDetails.companyId,
          gstNumber: companyDetails.gstin || '',
          msmeNumber: otherDetailsParsed.msmeNumber || '',
          panNumber: otherDetailsParsed.panNumber || '',
          registrationType: otherDetailsParsed.registrationType || 'gstin',
          registrationNumber: otherDetailsParsed.registrationNumber || '',
          businessType: otherDetailsParsed.businessType || '',
          industry: companyDetails.industry,
          companySize: companyDetails.empStrength,
          foundedYear: otherDetailsParsed.foundedYear || '',
          website: companyDetails.webpageUrl,
          description: companyDetails.description,
          address: companyDetails.address,
          city: otherDetailsParsed.officeCity || '',
          state: otherDetailsParsed.officeState || '',
          pincode: otherDetailsParsed.officePincode || '',
          country: otherDetailsParsed.officeCountry || '',
          contactPerson: otherDetailsParsed.primaryContactPerson || '',
          contactEmail: otherDetailsParsed.primaryContactEmail || '',
          contactPhone: otherDetailsParsed.primaryContactPhone || '',
          hrPerson: otherDetailsParsed.hrContactEmail || '',
          hrEmail: otherDetailsParsed.hrContactEmail || '',
          hrPhone: otherDetailsParsed.hrContactPhone || '',
          linkedinUrl: otherDetailsParsed.linkedinUrl || '',
          twitterUrl: otherDetailsParsed.twitterUrl || '',
          facebookUrl: otherDetailsParsed.facebookUrl || '',
          instagramUrl: otherDetailsParsed.instagramUrl || '',
          attachments: companyDetails.attachments || [],
          isVerified: companyDetails.verificationStatus === 'Verified',
          verificationStatus: companyDetails.verificationStatus,
          registrationStep: otherDetailsParsed.registrationStep || 0,
          shouldSubmitProfile: otherDetailsParsed.shouldSubmitProfile || false,
        };
        setCompanyData(mappedCompanyData);
      } catch (err) {
        setError("Failed to fetch company details.");
        console.error("Error fetching company details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, [companyId]);

  const getVerificationStatusColor = (status: CompanyVerificationStatus): string => {
    switch (status) {
      case 'Verified': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Blocked': return 'bg-gray-100 text-gray-800';
      case 'Incomplete': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationStatusText = (status: CompanyVerificationStatus): string => {
    switch (status) {
      case 'Verified': return 'Verified';
      case 'Pending': return 'Pending Review';
      case 'Rejected': return 'Rejected';
      case 'Blocked': return 'Blocked';
      case 'Incomplete': return 'Incomplete';
      default: return 'Not Submitted';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">Error: {error}</div>
    );
  }

  if (!companyData) {
    return (
      <div className="text-center py-12 text-gray-600">No company data found.</div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Profile</h1>
            <p className="text-gray-600">Complete overview of your company information</p>
          </div>
          <div className="flex space-x-3">
            <Link to={`/company/profile/edit`}>
              <Button variant="outline">Edit Profile</Button>
            </Link>
            <Button>Download Profile</Button>
          </div>
        </div>
        
        {/* Profile Completion */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Profile Completion</h2>
            <span className="text-sm text-gray-500">{companyData.registrationStep || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen h-3 rounded-full transition-all duration-500"
              style={{ width: `${companyData.registrationStep || 0}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {(companyData.registrationStep || 0) < 100 
              ? 'Complete the remaining sections to increase your company visibility'
              : 'Your company profile is complete and visible to job seekers!'
            }
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Company Info & Contact */}
        <div className="lg:col-span-1 space-y-6">
          {/* Company Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
            <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-4 overflow-hidden">
                {companyData.attachments && companyData.attachments.find(att => att.typeId === 'CompanyLogo')?.fileUrl ? (
                  <img src={companyData.attachments.find(att => att.typeId === 'CompanyLogo')?.fileUrl} alt="Company Logo" className="w-full h-full object-contain" />
                ) : (
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900">{companyData.companyName}</h2>
              <p className="text-gray-600 mb-2">{companyData.industry || 'N/A'} • {companyData.companySize || 'N/A'} employees</p>
              <Badge variant="primary" className="mb-4">
                {getVerificationStatusText(companyData.verificationStatus as CompanyVerificationStatus)}
              </Badge>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm text-gray-600">{companyData.contactEmail || 'N/A'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                </svg>
                {companyData.website ? (
                  <a href={companyData.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-lightGreen hover:underline">
                    {companyData.website}
                  </a>
                ) : (
                  <span className="text-sm text-gray-600">N/A</span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-gray-600">
                  {companyData.city || 'N/A'}, {companyData.state || 'N/A'}
                </span>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Social Media</h3>
              <div className="space-y-2">
                {companyData.linkedinUrl && (
                  <a href={companyData.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                )}
                {companyData.twitterUrl && (
                  <a href={companyData.twitterUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                    <span>Twitter</span>
                  </a>
                )}
                {companyData.facebookUrl && (
                  <a href={companyData.facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span>Facebook</span>
                  </a>
                )}
                {companyData.instagramUrl && (
                  <a href={companyData.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z"/>
                    </svg>
                    <span>Instagram</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Company Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Founded</span>
                <span className="text-sm font-medium text-gray-900">{companyData.foundedYear}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Employees</span>
                <span className="text-sm font-medium text-gray-900">{companyData.companySize}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Departments</span>
                <span className="text-sm font-medium text-gray-900">N/A</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Locations</span>
                <span className="text-sm font-medium text-gray-900">N/A</span>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Company Information</span>
                <Badge variant="warning" size="sm">{companyData.verificationStatus}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Company Details</span>
                <Badge variant="warning" size="sm">{companyData.verificationStatus}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Contact Information</span>
                <Badge variant="warning" size="sm">{companyData.verificationStatus}</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Detailed Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About Us</h3>
            <p className="text-gray-700 leading-relaxed mb-4">{companyData.description}</p>
            
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">Mission Statement</h4>
              <p className="text-gray-700 leading-relaxed mb-4">N/A</p>
            </div>
            
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">Company Values</h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" size="sm">N/A</Badge>
              </div>
            </div>
          </div>

          {/* Work Environment */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Environment</h3>
            <p className="text-gray-700 leading-relaxed">N/A</p>
          </div>

          {/* Benefits & Perks */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits & Perks</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">N/A</h4>
                  <p className="text-sm text-gray-600">N/A</p>
                </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Team Members</h3>
            <div className="space-y-4">
                <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-primary-lightGreen rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-black">
                      N/A
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">N/A</h4>
                    <p className="text-sm text-gray-600">N/A</p>
                  </div>
                  <div className="text-sm text-gray-500">N/A</div>
                </div>
            </div>
          </div>

          {/* Legal Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Legal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">GST Number</h4>
                <p className="text-sm text-gray-900">{companyData.gstNumber}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">PAN Number</h4>
                <p className="text-sm text-gray-900">{companyData.panNumber}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Registration Number</h4>
                <p className="text-sm text-gray-900">{companyData.registrationNumber}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">CIN Number</h4>
                <p className="text-sm text-gray-900">N/A</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Business Type</h4>
                <p className="text-sm text-gray-900">{companyData.businessType}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Trade License</h4>
                <p className="text-sm text-gray-900">N/A</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Primary Contact</h4>
                <div className="space-y-1">
                  <p className="text-sm text-gray-900">{companyData.contactPerson}</p>
                  <p className="text-sm text-gray-600">{companyData.contactEmail}</p>
                  <p className="text-sm text-gray-600">{companyData.contactPhone}</p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">HR Contact</h4>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">{companyData.hrEmail}</p>
                  <p className="text-sm text-gray-600">{companyData.hrPhone}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Office Address</h4>
              <div className="text-sm text-gray-900">
                <p>{companyData.address}</p>
                <p>{companyData.city}, {companyData.state} - {companyData.pincode}</p>
                <p>{companyData.country}</p>
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Profile Visibility</h4>
                  <p className="text-sm text-gray-500">Make company profile visible to job seekers</p>
                </div>
                <div className={`w-11 h-6 rounded-full ${companyData.shouldSubmitProfile ? 'bg-green-500' : 'bg-gray-200'} flex items-center justify-end px-1`}>
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Job Posting</h4>
                  <p className="text-sm text-gray-500">Allow posting jobs on the platform</p>
                </div>
                <div className={`w-11 h-6 rounded-full ${companyData.shouldSubmitProfile ? 'bg-green-500' : 'bg-gray-200'} flex items-center justify-end px-1`}>
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>
                  <p className="text-sm text-gray-500">Receive notifications about applications</p>
                </div>
                <div className={`w-11 h-6 rounded-full ${companyData.shouldSubmitProfile ? 'bg-green-500' : 'bg-gray-200'} flex items-center justify-end px-1`}>
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileSummary;
